import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL, urls } from './config.dev';

const ManageCategories = () => {
  const [employee, setEmployee] = useState('');
  const [clinic, setClinic] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    categoryName: '',
    categoryType: 'sales'
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');

        if (!tokenFromUrl) {
          console.error('Token not found in URL');
          toast.error('Authentication token missing');
          navigate('/login');
          return;
        }

        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();

          if (securityData.message === 'Session valid') {
            setEmployee(securityData.employee_name);
            fetchCategoriesData(tokenFromUrl);
          } else if (securityData.error === 'Session expired') {
            toast.error('Session expired. Please login again.');
            navigate('/login');
          } else {
            toast.error('Authentication failed');
            navigate('/login');
          }
        } else {
          throw new Error('Failed to perform security check');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        toast.error('Authentication error');
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  const fetchCategoriesData = async (token) => {
    try {
      setLoading(true);
      const response = await fetch(urls.fetchcategories, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setClinic(data.clinic);
          const mappedCategories = data.categories.map(category => ({
            id: category.id,
            name: category.category_name,
            type: category.type
          }));
          setCategories(mappedCategories);
        } else {
          throw new Error('Failed to fetch categories');
        }
      } else {
        throw new Error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (!token) {
      toast.error('Authentication token missing');
      navigate('/login');
      return;
    }

    if (!formData.categoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    
    const payload = {
      token,
      categoryName: formData.categoryName.trim(),
      categoryType: formData.categoryType
    };

    if (editingCategory) {
      payload.categoryId = editingCategory.id;
      payload.command = 'edit';
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(urls.setcategories, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(editingCategory ? 'Category updated successfully' : 'Category added successfully');
        setFormData({
          categoryName: '',
          categoryType: 'sales'
        });
        setEditingCategory(null);
        fetchCategoriesData(token);
      } else {
        throw new Error(editingCategory ? 'Failed to update category' : 'Failed to add category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error(editingCategory ? 'Failed to update category' : 'Failed to add category');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      categoryName: category.name,
      categoryType: category.type
    });
    
    document.getElementById('category-form').scrollIntoView({ behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setFormData({
      categoryName: '',
      categoryType: 'sales'
    });
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    
    if (!token) {
      toast.error('Authentication token missing');
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(urls.deletecategory, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, categoryId }),
      });

      if (response.ok) {
        toast.success('Category deleted successfully');
        fetchCategoriesData(token);
      } else {
        throw new Error('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  return (
    <div style={styles.container}>
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.title}>Category Management</h1>
            <p style={styles.subtitle}>{clinic}</p>
          </div>
          {employee && (
            <div style={styles.userInfo}>
              <span style={styles.userIcon}>👤</span>
              <span style={styles.userName}>{employee}</span>
            </div>
          )}
        </div>
      </header>
      
      <div style={styles.content}>
        <section id="category-form" style={styles.formSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              {editingCategory ? '✏️ Edit Category' : '➕ Add New Category'}
            </h2>
            <div style={styles.statsBadge}>
              Total Categories: {categories.length}
            </div>
          </div>
          
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label htmlFor="categoryName" style={styles.label}>
                  Category Name <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="categoryName"
                  name="categoryName"
                  value={formData.categoryName}
                  onChange={handleInputChange}
                  style={styles.input}
                  placeholder="e.g., Equipment, Supplies, Services"
                />
              </div>
              
              <div style={styles.formGroup}>
                <label htmlFor="categoryType" style={styles.label}>
                  Category Type <span style={styles.required}>*</span>
                </label>
                <select
                  id="categoryType"
                  name="categoryType"
                  value={formData.categoryType}
                  onChange={handleInputChange}
                  style={styles.select}
                >
                  <option value="sales">💰 For Sales (Revenue)</option>
                  <option value="expense">📉 For Expense (Cost)</option>
                </select>
              </div>
            </div>
            
            <div style={styles.buttonGroup}>
              <button 
                type="submit" 
                style={editingCategory ? styles.updateButton : styles.submitButton}
                disabled={submitting}
              >
                {submitting 
                  ? (editingCategory ? 'Updating...' : 'Adding...') 
                  : (editingCategory ? 'Update Category' : 'Add Category')
                }
              </button>
              
              {editingCategory && (
                <button 
                  type="button" 
                  onClick={handleCancelEdit}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>
        
        <section style={styles.tableSection}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>📋 Current Categories</h2>
          </div>
          
          {loading ? (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loading}>Loading categories...</p>
            </div>
          ) : categories.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>📂</div>
              <p style={styles.emptyText}>No categories found</p>
              <p style={styles.emptySubtext}>Add your first category using the form above</p>
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.tableHeaderRow}>
                    <th style={styles.tableHeader}>Category Name</th>
                    <th style={styles.tableHeader}>Type</th>
                    <th style={styles.tableHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id} style={styles.tableRow}>
                      <td style={styles.tableCell}>
                        <span style={styles.categoryName}>{category.name}</span>
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{
                          ...styles.typeBadge,
                          ...(category.type === 'sales' ? styles.salesBadge : styles.expenseBadge)
                        }}>
                          {category.type === 'sales' ? '💰 Sales' : '📉 Expense'}
                        </span>
                      </td>
                      <td style={styles.tableCell}>
                        <button 
                          onClick={() => handleEdit(category)}
                          style={styles.editButton}
                        >
                          ✏️ Edit
                        </button>
                        <button 
                          onClick={() => handleDelete(category.id)}
                          style={styles.deleteButton}
                        >
                          🗑️ Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
               </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const styles = {
  container: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
    padding: '0',
    color: '#1e293b',
  },
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    padding: '0 32px',
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#0f172a',
    margin: '0 0 8px 0',
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
    fontWeight: '500',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#f8fafc',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #e2e8f0',
  },
  userIcon: {
    fontSize: '18px',
  },
  userName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '32px',
  },
  formSection: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: '32px',
    overflow: 'hidden',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#fafbfc',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#0f172a',
    margin: '0',
  },
  statsBadge: {
    backgroundColor: '#e2e8f0',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
  },
  form: {
    padding: '24px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#334155',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    padding: '10px 14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    transition: 'all 0.2s',
    outline: 'none',
    fontFamily: 'inherit',
  },
  select: {
    padding: '10px 14px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s',
    outline: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px',
  },
  submitButton: {
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
  },
  updateButton: {
    backgroundColor: '#10b981',
    color: '#ffffff',
    border: 'none',
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: '#059669',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    },
  },
  cancelButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569',
    border: '1px solid #cbd5e1',
    padding: '10px 24px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: '#e2e8f0',
    },
  },
  tableSection: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#3b82f6',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loading: {
    color: '#64748b',
    marginTop: '16px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px',
  },
  emptyText: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#334155',
    margin: '0 0 8px 0',
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#64748b',
    margin: '0',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeaderRow: {
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
  },
  tableHeader: {
    padding: '16px 24px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    borderBottom: '1px solid #f1f5f9',
    transition: 'background-color 0.2s',
    ':hover': {
      backgroundColor: '#fafbfc',
    },
  },
  tableCell: {
    padding: '16px 24px',
    fontSize: '14px',
    color: '#334155',
  },
  categoryName: {
    fontWeight: '500',
  },
  typeBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  salesBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  expenseBadge: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  editButton: {
    backgroundColor: '#fef3c7',
    color: '#92400e',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    marginRight: '8px',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: '#fde68a',
    },
  },
  deleteButton: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: 'none',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
    ':hover': {
      backgroundColor: '#fecaca',
    },
  },
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  
  button:hover {
    transform: translateY(-1px);
  }
  
  input:focus, select:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;
document.head.appendChild(styleSheet);

export default ManageCategories;